/**
 * Keyboard-awareness for the Host Console.
 *
 * On iPad/iPhone (and any browser with the VisualViewport API) the soft
 * keyboard only shrinks the *visual* viewport: `position: fixed` overlays
 * such as the react-bootstrap modals keep their full layout-viewport height,
 * so the focused field - and the modal footer - end up hidden underneath the
 * keyboard. iOS also refuses to scroll focused elements that live inside an
 * `overflow: auto` container nested in a fixed element, which means the host
 * can type into a field they cannot see.
 *
 * This module:
 *  1. publishes the live keyboard height as the `--tm-kb` CSS custom property
 *     (and toggles `body.has-keyboard`) so layouts can reserve that space;
 *  2. keeps the focused input visible by scrolling the nearest scrollable
 *     ancestor (modal body / overlay) until the field sits centered in the
 *     area above the keyboard.
 *
 * It degrades to a no-op on browsers without `window.visualViewport`.
 */

const FIELD_SELECTOR = 'input, textarea, [contenteditable="true"]';

// Anything smaller than this is treated as URL-bar chrome, not a keyboard.
const KEYBOARD_THRESHOLD = 110;

// The keyboard animates in over ~250-400ms; retry until the inset stabilises.
const FOLLOW_MAX_MS = 2500;
const FOLLOW_STEP_MS = 120;

const NON_TEXT_TYPES = ['button', 'submit', 'reset', 'checkbox', 'radio', 'hidden', 'range', 'color', 'file'];

const state = {
  installed: false,
  activeField: null,
  followTimer: 0,
  lastInset: 0,
  settledInset: -1,
};

const isField = (node) => {
  if (!node || !node.matches) return false;
  if (node.isContentEditable === true) return true;
  if (!node.matches(FIELD_SELECTOR)) return false;
  if (node.disabled || node.readOnly) return false;
  const type = String(node.type || '').toLowerCase();
  return !NON_TEXT_TYPES.includes(type);
};

const keyboardInset = () => {
  const vv = window.visualViewport;
  if (!vv) return 0;
  const inset = window.innerHeight - vv.height - vv.offsetTop;
  return inset > KEYBOARD_THRESHOLD ? Math.round(inset) : 0;
};

const applyInset = () => {
  const inset = keyboardInset();
  state.lastInset = inset;
  document.documentElement.style.setProperty('--tm-kb', inset + 'px');
  // dataset mirror: debuggable in DevTools and readable by test environments
  // whose DOM implementation drops unknown CSS custom properties.
  if (document.body.dataset) document.body.dataset.tmKb = String(inset);
  document.body.classList.toggle('has-keyboard', inset > 0);
  return inset;
};

/** Bottom edge (CSS px) of the area not covered by the keyboard. */
const visibleBottom = () => {
  const vv = window.visualViewport;
  if (vv) return vv.height + vv.offsetTop;
  return window.innerHeight;
};

const scrollableAncestor = (node) => {
  let current = node.parentElement;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    if (/(auto|scroll|overlay)/.test(style.overflowY) && current.scrollHeight > current.clientHeight + 1) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

const bringFieldIntoView = (field) => {
  if (!field || !field.isConnected) return;
  const bottom = visibleBottom() - 14;
  const rect = field.getBoundingClientRect();
  if (rect.height > 0 && rect.top >= 10 && rect.bottom <= bottom) return; // already clear of the keyboard

  const container = scrollableAncestor(field);
  if (container) {
    const cRect = container.getBoundingClientRect();
    const visibleTop = Math.max(cRect.top, 10);
    const visibleEnd = Math.min(cRect.bottom, bottom);
    const trackHeight = Math.max(visibleEnd - visibleTop, rect.height + 24);
    const target = container.scrollTop + (rect.top - cRect.top) - trackHeight / 2 + rect.height / 2;
    container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  } else if (typeof field.scrollIntoView === 'function') {
    // Plain page flow (e.g. the sign-in card): the document itself scrolls.
    field.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
};

/** Re-run the visibility check while the keyboard finishes animating in. */
const followField = (startedAt) => {
  const started = startedAt || Date.now();
  window.clearTimeout(state.followTimer);
  const tick = () => {
    if (!state.activeField) return;
    const inset = applyInset();
    bringFieldIntoView(state.activeField);
    // Two identical non-zero samples in a row = the keyboard finished animating.
    if (inset > 0 && inset === state.settledInset) return;
    state.settledInset = inset;
    if (Date.now() - started > FOLLOW_MAX_MS) return;
    state.followTimer = window.setTimeout(tick, FOLLOW_STEP_MS);
  };
  state.followTimer = window.setTimeout(tick, FOLLOW_STEP_MS);
};

const onFocusIn = (event) => {
  if (!isField(event.target)) return;
  state.activeField = event.target;
  state.settledInset = -1;
  followField(Date.now());
};

const onFocusOut = () => {
  state.activeField = null;
  window.clearTimeout(state.followTimer);
  // Give the keyboard a beat to dismiss before releasing reserved space.
  window.setTimeout(() => {
    if (!state.activeField) applyInset();
  }, 220);
};

const onViewportChange = () => {
  applyInset();
  if (state.activeField) bringFieldIntoView(state.activeField);
};

export const install = () => {
  if (state.installed) return;
  state.installed = true;
  document.documentElement.style.setProperty('--tm-kb', '0px');
  const vv = window.visualViewport;
  if (vv) {
    vv.addEventListener('resize', onViewportChange);
    vv.addEventListener('scroll', onViewportChange);
  }
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('focusout', onFocusOut);
  applyInset();
};

export const uninstall = () => {
  if (!state.installed) return;
  state.installed = false;
  state.activeField = null;
  window.clearTimeout(state.followTimer);
  const vv = window.visualViewport;
  if (vv) {
    vv.removeEventListener('resize', onViewportChange);
    vv.removeEventListener('scroll', onViewportChange);
  }
  document.removeEventListener('focusin', onFocusIn);
  document.removeEventListener('focusout', onFocusOut);
  document.documentElement.style.setProperty('--tm-kb', '0px');
  document.body.classList.remove('has-keyboard');
};

export default { install, uninstall };
